import React from 'react';

// Otto seasonal accessory PROPS (not costumes) — owl-shaped <g>
// fragments authored for Otto's 120×132 viewBox, matching Turbo's 10
// occasions. Rendered by OttoAlive on the everyday DEFAULT pose only,
// resolved by the SHARED seasonal machinery (turbo/seasonal — schedule,
// priorities, Easter, opt-out reused unchanged). Goggles are kept in
// every variant; the base head (cap vs bare) is chosen by each season's
// `replacesCap` flag in turboSeasons. Colours resolve from the global
// --fusion-* tokens + the .otto-svg palette. Two animate (Autumn leaves,
// Christmas snow) via otto.css classes with a reduced-motion fallback.

// 1 · New Year — party hat (replaces cap)
export const NewYearAccessory = () => (
  <g aria-hidden="true">
    <path d="M60 -2 l15 42 q-15 6 -30 0z" fill="var(--fusion-purple)" />
    <path d="M60 -2 l5 15 l-8 10 l7 12 l-7 6" stroke="var(--fusion-amber)" strokeWidth="2.4" fill="none" />
    <circle cx="60" cy="-2" r="5" fill="var(--fusion-amber)" />
    <circle cx="48" cy="28" r="1.8" fill="#fff" /><circle cx="70" cy="22" r="1.8" fill="#fff" /><circle cx="64" cy="34" r="1.6" fill="var(--fusion-teal)" />
  </g>
);

// 2 · Winter — bobble hat (replaces cap)
export const WinterAccessory = () => (
  <g aria-hidden="true">
    <path d="M34 40 q26 -30 52 0 q-26 9 -52 0z" fill="var(--fusion-teal)" />
    <rect x="32" y="38" width="56" height="9" rx="4" fill="#fbfdf6" />
    <circle cx="60" cy="10" r="7" fill="#fbfdf6" />
  </g>
);

// 3 · Valentine's — floating heart + scattered hearts (keeps cap)
export const ValentinesAccessory = () => (
  <g aria-hidden="true">
    <path d="M60 18 q-6 -10 -14 -6 q-8 5 -2 14 q4 6 16 14 q12 -8 16 -14 q6 -9 -2 -14 q-8 -4 -14 6z" fill="var(--fusion-red)" />
    <circle cx="52" cy="15" r="1.4" fill="#fff" opacity=".8" />
    <g fill="var(--fusion-red)">
      <path d="M20 40 q-2 -3.5 -5 -2 q-3 2 -1 5 q1.5 2 6 5 q4.5 -3 6 -5 q2 -3 -1 -5 q-3 -1.5 -5 2z" opacity=".85" />
      <path d="M100 44 q-1.6 -2.8 -4 -1.6 q-2.4 1.6 -.8 4 q1.2 1.6 4.8 4 q3.6 -2.4 4.8 -4 q1.6 -2.4 -.8 -4 q-2.4 -1.2 -4 1.6z" opacity=".7" />
      <path d="M96 18 q-1.3 -2.3 -3.3 -1.3 q-2 1.3 -.7 3.3 q1 1.3 4 3.3 q3 -2 4 -3.3 q1.3 -2 -.7 -3.3 q-2 -1 -3.3 1.3z" opacity=".6" />
      <path d="M26 66 q-1.3 -2.3 -3.3 -1.3 q-2 1.3 -.7 3.3 q1 1.3 4 3.3 q3 -2 4 -3.3 q1.3 -2 -.7 -3.3 q-2 -1 -3.3 1.3z" opacity=".55" />
    </g>
  </g>
);

// 4 · Spring — flower crown across the cap (keeps cap)
const Flower = ({ x, y, petals }) => (
  <g transform={`translate(${x} ${y})`}>
    <g fill={petals}>
      <ellipse cx="0" cy="-4" rx="2.2" ry="3.6" /><ellipse cx="4" cy="0" rx="3.6" ry="2.2" />
      <ellipse cx="0" cy="4" rx="2.2" ry="3.6" /><ellipse cx="-4" cy="0" rx="3.6" ry="2.2" />
    </g>
    <circle r="2.4" fill="var(--fusion-amber)" />
  </g>
);
export const SpringAccessory = () => (
  <g aria-hidden="true">
    <path d="M31 44 q29 -16 58 0" stroke="#5a7d3a" strokeWidth="2.5" fill="none" />
    <Flower x={36} y={42} petals="#fbfdf6" />
    <Flower x={50} y={35} petals="#f2b8c6" />
    <Flower x={70} y={35} petals="var(--fusion-purple)" />
    <Flower x={84} y={42} petals="#fbfdf6" />
    <circle cx="43" cy="37" r="1.6" fill="var(--fusion-green)" /><circle cx="60" cy="31" r="1.6" fill="var(--fusion-green)" /><circle cx="78" cy="37" r="1.6" fill="var(--fusion-green)" />
  </g>
);

// 5 · Easter — bunny ears (replaces cap)
export const EasterAccessory = () => (
  <g aria-hidden="true">
    <path d="M46 44 q-9 -40 1 -46 q9 -4 8 15 q-1 17 -5 31z" fill="#fbfdf6" stroke="var(--brown-d)" strokeWidth="1.4" />
    <path d="M48 42 q-4 -28 1 -37" stroke="#f2b8c6" strokeWidth="5" fill="none" />
    <path d="M74 44 q9 -40 -1 -46 q-9 -4 -8 15 q1 17 5 31z" fill="#fbfdf6" stroke="var(--brown-d)" strokeWidth="1.4" />
    <path d="M72 42 q4 -28 -1 -37" stroke="#f2b8c6" strokeWidth="5" fill="none" />
  </g>
);

// 6 · Summer — smiling sun, upper-left (independent, keeps cap)
export const SummerAccessory = () => (
  <g aria-hidden="true">
    <g transform="translate(20 20)">
      <circle r="11" fill="var(--fusion-amber)" />
      <g stroke="var(--fusion-amber)" strokeWidth="2.4" strokeLinecap="round"><path d="M0 -16v-5M0 16v5M-16 0h-5M16 0h5M-11 -11l-4 -4M11 11l4 4M11 -11l4 -4M-11 11l-4 4" /></g>
      <path d="M-4 2 q4 4 8 0" stroke="#7a5a10" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <circle cx="-4" cy="-2" r="1.2" fill="#7a5a10" /><circle cx="4" cy="-2" r="1.2" fill="#7a5a10" />
    </g>
  </g>
);

// 7 · Autumn — falling leaves drifting around Otto (keeps cap, animated)
const Leaf = ({ x, cls, scale, fill, vein }) => (
  <g transform={`translate(${x} 0)`}>
    <g className={`otto-leaf ${cls}`}>
      <g transform={`scale(${scale})`}>
        <path d="M0 8 C -6 4 -7 -2 -1 -8 C 0 -9 0 -9 1 -8 C 7 -2 6 4 0 8 Z" fill={fill} />
        <path d="M0 7 V-6 M0 0 l-4 -2 M0 0 l4 -2" stroke={vein} strokeWidth=".8" fill="none" />
      </g>
    </g>
  </g>
);
export const AutumnAccessory = () => (
  <g aria-hidden="true">
    <Leaf x={30} cls="otto-lf1" scale={0.85} fill="var(--fusion-red)" vein="#8a1c08" />
    <Leaf x={58} cls="otto-lf2" scale={0.8} fill="var(--fusion-amber)" vein="#a86a1a" />
    <Leaf x={84} cls="otto-lf3" scale={0.7} fill="#c9781f" vein="#7a3d0a" />
    <Leaf x={44} cls="otto-lf4" scale={0.75} fill="var(--fusion-red)" vein="#8a1c08" />
    <Leaf x={72} cls="otto-lf5" scale={0.7} fill="var(--fusion-amber)" vein="#a86a1a" />
  </g>
);

// 8 · Halloween — pumpkin at foot level in front (keeps cap)
export const HalloweenAccessory = () => (
  <g aria-hidden="true">
    <g transform="translate(60 116)">
      <ellipse cx="0" cy="0" rx="20" ry="16" fill="var(--fusion-amber)" />
      <path d="M-13 0 q0 -14 5 -15 M-6 0 q0 -15 3 -16 M0 0 v-16 M6 0 q0 -15 -3 -16 M13 0 q0 -14 -5 -15" stroke="#c9781f" strokeWidth="1.4" fill="none" />
      <rect x="-2.5" y="-21" width="5" height="6" fill="#5a7d3a" />
      <path d="M-9 -3 l4 -5 l4 5z M1 -3 l4 -5 l4 5z" fill="#3a2a12" />
      <path d="M-7 6 q7 6 14 0" stroke="#3a2a12" strokeWidth="2" fill="none" />
      <path d="M-3 6 v4 M3 6 v4" stroke="#3a2a12" strokeWidth="1.6" />
    </g>
  </g>
);

// 9 · Bonfire Night — firework bursts (independent, keeps cap)
export const BonfireAccessory = () => (
  <g aria-hidden="true">
    <g transform="translate(90 24)">
      <g strokeWidth="2.4" strokeLinecap="round" opacity=".95">
        <path d="M0 0 l0 -15" stroke="var(--fusion-red)" /><path d="M0 0 l0 15" stroke="var(--fusion-amber)" />
        <path d="M0 0 l-15 0" stroke="var(--fusion-teal)" /><path d="M0 0 l15 0" stroke="var(--fusion-green)" />
        <path d="M0 0 l-11 -11" stroke="var(--fusion-purple)" /><path d="M0 0 l11 -11" stroke="var(--fusion-amber)" />
        <path d="M0 0 l-11 11" stroke="var(--fusion-green)" /><path d="M0 0 l11 11" stroke="var(--fusion-red)" />
        <path d="M0 0 l-6 -14" stroke="var(--fusion-teal)" /><path d="M0 0 l6 -14" stroke="var(--fusion-purple)" />
        <path d="M0 0 l-14 -6" stroke="var(--fusion-amber)" /><path d="M0 0 l14 -6" stroke="var(--fusion-red)" />
        <path d="M0 0 l-14 6" stroke="var(--fusion-red)" /><path d="M0 0 l14 6" stroke="var(--fusion-teal)" />
        <path d="M0 0 l-6 14" stroke="var(--fusion-green)" /><path d="M0 0 l6 14" stroke="var(--fusion-amber)" />
      </g>
      <circle r="3.5" fill="#fbfdf6" />
      <g><circle cx="0" cy="-17" r="2" fill="var(--fusion-red)" /><circle cx="17" cy="0" r="2" fill="var(--fusion-green)" /><circle cx="-17" cy="0" r="2" fill="var(--fusion-teal)" /><circle cx="0" cy="17" r="2" fill="var(--fusion-amber)" /><circle cx="12" cy="-12" r="1.8" fill="var(--fusion-purple)" /><circle cx="-12" cy="12" r="1.8" fill="var(--fusion-green)" /></g>
    </g>
    <g transform="translate(26 40)">
      <g strokeWidth="2" strokeLinecap="round" opacity=".9">
        <path d="M0 0 l0 -10" stroke="var(--fusion-amber)" /><path d="M0 0 l0 10" stroke="var(--fusion-teal)" />
        <path d="M0 0 l-10 0" stroke="var(--fusion-purple)" /><path d="M0 0 l10 0" stroke="var(--fusion-red)" />
        <path d="M0 0 l-7 -7" stroke="var(--fusion-green)" /><path d="M0 0 l7 7" stroke="var(--fusion-amber)" />
        <path d="M0 0 l7 -7" stroke="var(--fusion-teal)" /><path d="M0 0 l-7 7" stroke="var(--fusion-red)" />
      </g>
      <circle r="2.4" fill="#fbfdf6" />
    </g>
  </g>
);

// 10 · Christmas — santa hat (replaces cap) + falling snow (animated)
const Snow = ({ x, cls, r }) => (
  <g transform={`translate(${x} 0)`}><circle className={`otto-snow ${cls}`} r={r} fill="#fbfdf6" /></g>
);
export const ChristmasAccessory = () => (
  <g aria-hidden="true">
    <Snow x={26} cls="otto-sn1" r={2.4} />
    <Snow x={50} cls="otto-sn2" r={1.8} />
    <Snow x={74} cls="otto-sn3" r={2.6} />
    <Snow x={94} cls="otto-sn4" r={1.6} />
    <Snow x={38} cls="otto-sn5" r={2} />
    <Snow x={62} cls="otto-sn6" r={1.6} />
    <Snow x={84} cls="otto-sn7" r={2.2} />
    <path d="M32 42 q10 -34 38 -30 q17 3 13 19 q-2 8 -12 11 q-21 8 -39 0z" fill="var(--fusion-red)" />
    <rect x="30" y="38" width="56" height="10" rx="5" fill="#fbfdf6" />
    <circle cx="80" cy="32" r="7" fill="#fbfdf6" />
  </g>
);
